import _, {Dictionary, identity, sortBy, uniqBy} from 'lodash';
import memoize from 'lru-memoize';

import {addQueryToPath} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {PredicateOf} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TagBehaviorsEnum} from '../../../libs/shared/core/src/models/api/commonTagModel';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {isTeammateNamespace} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {SearchTagModel} from '../../models/api/searchTagModel';
import {TagModel} from '../../models/api/tagModel';
import {ParentTagOverridesEnum, ParentTagOverridesMap} from '../visualCustomViewHelpers';

/*
 * Nesting.
 */

/** Find the nesting level of the specified tag. */
export function findTagNestingLevel(tags: ResourceMap<TagModel>, tag: TagModel, level = 0): number {
  const {parentTagId} = tag;
  const parent = parentTagId && tags[parentTagId];
  if (!parent) {
    return level;
  }

  return findTagNestingLevel(tags, parent, level + 1);
}

function findParentTagsOrNull(tags: ResourceMap<TagModel>, tag: TagModel): ReadonlyArray<TagModel | null> {
  const {parentTagId} = tag;

  if (!parentTagId) {
    return [];
  }

  const parent = tags[parentTagId];

  if (parent === undefined) {
    return [null];
  }

  return [parent, ...findParentTagsOrNull(tags, parent)];
}

export function everyParentTagIsAvailable(tags: ResourceMap<TagModel>, tag: TagModel): boolean {
  return findParentTagsOrNull(tags, tag).every((parentTag) => parentTag !== null);
}

/** Find the sorted array of parent tags for the specified tag. */
export function findParentTags(tags: ResourceMap<TagModel>, tag: TagModel): ReadonlyArray<TagModel> {
  const isTag = (parentTag: TagModel | null): parentTag is TagModel => parentTag !== null;
  return findParentTagsOrNull(tags, tag).filter(isTag);
}

/** Find the sorted array of parent tags, but also take into account nesting changes made in edit mode. */
export function findEditModeParentTags(
  tags: ResourceMap<TagModel>,
  parentTagOverridesMap: ParentTagOverridesMap,
  tag: TagModel,
): ReadonlyArray<TagModel> {
  const {id} = tag;

  // we haven't overridden the parent tag or there is no actual change in the parent
  if (!(id in parentTagOverridesMap) || parentTagOverridesMap[id] === ParentTagOverridesEnum.NO_CHANGE) {
    const parent = tag.parentTagId && tags[tag.parentTagId];
    if (!parent) {
      return [];
    }
    return [parent, ...findEditModeParentTags(tags, parentTagOverridesMap, parent)];
  }

  // we have changed the parent tag
  const updatedParentTagId = parentTagOverridesMap[id];
  if (updatedParentTagId === ParentTagOverridesEnum.MOVED_TO_ROOT) {
    return [];
  }
  const parent = updatedParentTagId && tags[updatedParentTagId];
  if (!parent) {
    return [];
  }
  return [parent, ...findEditModeParentTags(tags, parentTagOverridesMap, parent)];
}

interface FindChildTagIdsToRemoveParams {
  tagId: number;
  visibleTagIds: ReadonlyArray<number>;
  combinedTags: ResourceMap<TagModel>;
}

interface FindEditModeChildTagIdsToRemoveParams extends FindChildTagIdsToRemoveParams {
  parentTagOverridesMap: ParentTagOverridesMap;
}

/** Returns the child tag IDs to remove when untoggling a tag. */
export function findChildTagIdsToRemove(params: FindChildTagIdsToRemoveParams) {
  const {tagId, visibleTagIds, combinedTags} = params;
  return _(visibleTagIds)
    .filter((id) => {
      const tag = combinedTags[id];
      if (!tag) {
        return false;
      }

      const parents = findParentTags(combinedTags, tag);

      if (parents.length < 1) {
        return false;
      }

      if (!parents.some((t) => t.id === tagId)) {
        return false;
      }

      return true;
    })
    .concat(tagId)
    .value();
}

/** Returns the child tag IDs to remove when untoggling a tag in edit mode. */
export function findEditModeChildTagIdsToRemove(params: FindEditModeChildTagIdsToRemoveParams) {
  const {tagId, visibleTagIds, parentTagOverridesMap, combinedTags} = params;
  return _(visibleTagIds)
    .filter((id) => {
      const tag = combinedTags[id];
      if (!tag) {
        return false;
      }

      const parents = findEditModeParentTags(combinedTags, parentTagOverridesMap, tag);

      if (parents.length < 1) {
        return false;
      }

      if (!parents.some((t) => t.id === tagId)) {
        return false;
      }

      return true;
    })
    .concat(tagId)
    .value();
}

/** Find the sorted array of children tags for the specific tag. */
export function findTagChildren(
  tags: ResourceMap<TagModel>,
  tag: TagModel,
  considerArchived: boolean,
): ReadonlyArray<TagModel> {
  const childrenOfChildren = _(getTagChildrenIds(tag, considerArchived))
    .flatMap((tId) => {
      const childTag = tags[tId];

      if (!childTag) {
        return undefined;
      }

      return findTagChildren(tags, childTag, considerArchived);
    })
    .compact()
    .value();

  const firstChildren = _(getTagChildrenIds(tag, considerArchived))
    .map((id) => tags[id])
    .compact()
    .concat(childrenOfChildren)
    .value();

  return firstChildren;
}

/*
 * Search URL.
 */

export enum SearchTagSortsEnum {
  PATH_ASC = 'name',
  PATH_DESC = '-name',
  NAME_ASC = 'tag_name',
  NAME_DESC = '-tag_name',
  LAST_USED_AT_ASC = 'last_used_at',
  LAST_USED_AT_DESC = '-last_used_at',
  CREATED_AT_ASC = 'created_at',
  CREATED_AT_DESC = '-created_at',
  ALIAS_NAME = 'alias_name',
}

export enum SearchTagStatusesEnum {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  ALL = 'all',
}

export interface SearchTagOptions {
  /** Only list tags after the offset. */
  offset: number;
  /** Limits the number of tags returned in the response. */
  limit?: number;
  /** Only list tags available in those namespaces. */
  namespaces?: ReadonlyArray<string>;
  /** Only list tags of this type. */
  types?: ReadonlyArray<string>;
  /** Show tags that are only accessible in some inboxes. */
  inboxIds?: ReadonlyArray<number>;
  /** Exclude private tags from the results. */
  excludePrivateTags?: boolean;
  /** Exclude tags and their children from the results. */
  excludeTagIds?: ReadonlyArray<number>;
  /** Only show tags who have the right behaviors. */
  behaviors?: ReadonlyArray<TagBehaviorsEnum>;
  /** Only show tags who have the right behaviors, WITHOUT adding CAN_ALL by default to this list. */
  specifiedBehaviorsOnly?: boolean;
  /** Only return tags who are children of the given ids. */
  parentTagIds?: ReadonlyArray<number>;
  /** Only return tags who don't have parents. */
  shouldReturnParentsOnly?: boolean;
  status?: SearchTagStatusesEnum;
  /** Sort by a given field. */
  sort?: SearchTagSortsEnum;
  /** Whether we're searching tags while in the settings (passes `admin_override=true`) */
  isInSettingsContext?: boolean;
  /** Source to be used for tracking in related search queries. */
  source: string;
}

export function buildTagSearchUrl(company: CompanyModel, query: string, options: SearchTagOptions) {
  return addQueryToPath(
    `${company.url}/search_tag2/${encodeURIComponent(query.toLowerCase())}`,
    serializeSearchTagOptions(options),
  );
}

function serializeSearchTagOptions(options: SearchTagOptions) {
  return {
    admin_override: options.isInSettingsContext || false,
    offset: options.offset,
    limit: options.limit || 50,
    namespaces: (options.namespaces && options.namespaces.join(',')) || undefined,
    filter_type: (options.types && options.types.join(',')) || undefined,
    inbox_ids: (options.inboxIds && options.inboxIds.join(',')) || undefined,
    exclude_tags: (options.excludeTagIds && options.excludeTagIds.join(',')) || undefined,
    behaviors: (options.behaviors && options.behaviors.join(',')) || undefined,
    specified_behaviors_only: options.specifiedBehaviorsOnly,
    parent_tags: (options.parentTagIds && options.parentTagIds.join(',')) || undefined,
    root_tags_only: options.shouldReturnParentsOnly,
    exclude_private_tags: options.excludePrivateTags,
    tag_status: options.status ?? 'active',
    sort: options.sort,
    source: options.source,
  };
}

/*
 * Dropdowns.
 */

export enum ViewerDropdownInterfaceEventSourcesEnum {
  MULTI_SELECT_MODAL = 'multi-select-modal',
  TOPBAR = 'topbar',
}

/*
 * Behaviors.
 */

export function tagHasBehaviors(tag: TagModel, ...behaviors: Array<TagBehaviorsEnum>) {
  return behaviors.every((b) => tag.behaviors.includes(b));
}

export function sortTags(
  tags: ReadonlyArray<SearchTagModel>,
  baseSort: PredicateOf<SearchTagModel> = identity,
  targetIndex: number = 1,
) {
  // No tags passed in, return early
  if (tags.length === 0) {
    return [];
  }

  // Sort and filter tags to the current index
  const baseParentsSorted = sortBy(
    tags.filter((tag) => tag.length === targetIndex),
    baseSort,
  );

  // Construct the response array and check for children tags
  return baseParentsSorted.reduce((result, parentTag) => {
    result.push(parentTag);

    // If we have children tags, we need to sort them as well, while keeping them nested in the correct order
    const children = tags.filter((searchTag) => searchTag.some((tag) => tag.parentTagId === parentTag[0].id));
    if (children.length > 0) {
      result.push(...sortTags(children, baseSort, targetIndex + 1));
    }

    return result;
  }, [] as Array<SearchTagModel>);
}

export function buildVisualSearchTagsWithQuery(searchTags: ReadonlyArray<SearchTagModel>) {
  return _(searchTags)
    .map((t) => t[0])
    .uniqBy((t) => t.id)
    .map((t) => buildVisualSearchTag(() => true, {}, t))
    .value();
}

export function buildVisualSearchTags(
  searchTags: ReadonlyArray<SearchTagModel>,
  findIsExpanded: PredicateOf<number>,
  parentTagToIgnoreId = 0,
  isInArchivedView = false,
) {
  const parentTagIdsToIgnore = new Set([parentTagToIgnoreId]);
  const tagsByParent = _(searchTags)
    .flattenDeep()
    .filter((t) => {
      // If we're ignoring a parent, we want to ignore tags who don't have any parent as well.
      if (parentTagToIgnoreId) {
        return t.id !== parentTagToIgnoreId && Boolean(t.parentTagId);
      }

      return true;
    })
    .filter((t) => {
      if (isInArchivedView) {
        if (!t.archivedAt) {
          parentTagIdsToIgnore.add(t.id);
        }

        return Boolean(t.archivedAt);
      }

      return !t.archivedAt;
    })
    .uniqBy((t) => t.id)
    .groupBy((t) => {
      if (parentTagIdsToIgnore.has(t.parentTagId || 0)) {
        return 0;
      }

      return t.parentTagId || 0;
    })
    .mapValues((tArr) => uniqBy(tArr, (t) => t.id))
    .value();

  return _(tagsByParent[0])
    .map((t) => buildVisualSearchTag(findIsExpanded, tagsByParent, t))
    .map((t) => {
      if (t.isExpanded) {
        return [t, ...t.children];
      }

      return [t];
    })
    .flatten()
    .value();
}

export interface VisualSearchTag {
  id: number;
  tag: TagModel;
  isExpanded: boolean;
  children: ReadonlyArray<VisualSearchTag>;
}

function buildVisualSearchTag(
  findIsExpanded: PredicateOf<number>,
  tagsByParent: Dictionary<ReadonlyArray<TagModel>>,
  tag: TagModel,
): VisualSearchTag {
  const isExpanded = findIsExpanded(tag.id);
  const children = _(tagsByParent[tag.id])
    .map((t) => buildVisualSearchTag(findIsExpanded, tagsByParent, t))
    .map((t) => {
      if (t.isExpanded) {
        return [t, ...t.children];
      }

      return [t];
    })
    .flatten()
    .value();

  return {
    id: tag.id,
    tag,
    isExpanded,
    children,
  };
}

export const getTagAliasDuplicates = memoize()((searchTags: ReadonlyArray<SearchTagModel | null>) => {
  const tagAliases = new Set<string>();
  const tagAliasDuplicates = new Set<string>();

  // Loop on all the tags to find the ones with duplicate names.
  searchTags.forEach((searchTag) => {
    if (!searchTag) {
      return;
    }

    const tag = searchTag[0];
    if (tagAliases.has(tag.alias)) {
      tagAliasDuplicates.add(tag.alias);
      return;
    }

    tagAliases.add(tag.alias);
  });

  return tagAliasDuplicates;
});

export function getTagChildrenIds(tag: TagModel, considerArchived?: boolean) {
  return (considerArchived ? tag.archivedChildTagIds : tag.activeChildTagIds) || tag.childTagIds;
}

export const isPrivateTag = (tag: TagModel): boolean => isTeammateNamespace(tag.namespace);

export const isArchivedTag = (tag: TagModel): boolean => Boolean(tag.archivedAt);

export const TAG_NAME_MAX_LENGTH = 64;
